import { useEffect, useMemo, useRef } from "react";
import styles from './identity.module.scss';
import { IdentityProps } from "../../../../interfaces/components";
import { useDocumentType, useToast } from "../../../../helpers/hooks";
import { useLocation } from "react-router-dom";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CircularProgress } from "@mui/material";
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';

const SLOTS = [
    { label: 'frente' },
    { label: 'reverso' },
];

const LS_KEY = 'identity_card_slots';

const readLocalSlots = (): Record<string, number> => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '{}'); }
    catch { return {}; }
};

const writeLocalSlots = (map: Record<string, number>) => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
};

const Identity = ({
    types,
    setDocuments,
    getDocuments,
    fetchingDocuments,
    documents,
    cleanGetDocuments,
    getIsValidDocuments,
    postDocuments,
    fetchingPostDocuments,
    cleanPostDocuments,
    postDocumentImage,
    fetchingPostDocumentImage,
    cleanPostDocumentImage,
    deleteDocumentImage,
    fetchingDeleteDocumentImage,
    cleanDeleteDocumentImage,
    deleteDocument,
    fetchingDeleteDocument,
    cleanDeleteDocument
}: IdentityProps) => {
    const toast = useToast();
    const location = useLocation();
    const documentType = useDocumentType();

    // url → slot mapping, populated before postDocuments so that after the
    // reducer replaces local docs with API response docs (which have the same
    // url but no slotIndex), useMemo can recover the correct slot.
    const urlSlotRef = useRef<Record<string, number>>({});
    const savedKeyRef = useRef<string>("");

    const activeDocs: any[] = useMemo(() => {
        const stored = readLocalSlots();
        return documents
            .filter((d: any) => !d?.toDelete)
            .map((d: any, i: number) => {
                if (d.slotIndex !== undefined) return d;
                if (d._id && stored[d._id] !== undefined) return { ...d, slotIndex: stored[d._id] };
                if (d.url && urlSlotRef.current[d.url] !== undefined) return { ...d, slotIndex: urlSlotRef.current[d.url] };
                if (d.name?.includes('slot0_')) return { ...d, slotIndex: 0 };
                if (d.name?.includes('slot1_')) return { ...d, slotIndex: 1 };
                return { ...d, slotIndex: i };
            });
    }, [documents]);

    // Persist _id → slot in localStorage for all detectable API docs
    useEffect(() => {
        const apiDocs = activeDocs.filter((d: any) => d._id && d.slotIndex !== undefined);
        if (apiDocs.length === 0) return;
        const stored = readLocalSlots();
        apiDocs.forEach((d: any) => { stored[d._id] = d.slotIndex; });
        writeLocalSlots(stored);
    }, [activeDocs]);

    const processAndUpload = (file: File, slotIndex: number, currentDocs: any[]) => {
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast("Archivo con formato inválido");
            return;
        }
        if (file.size === 0 || file.size > 10 * 1024 * 1024) {
            toast("Archivo inválido o demasiado grande");
            return;
        }
        const newDoc = {
            file,
            id: Math.floor(Math.random() * 0x10000).toString(16),
            previewURL: URL.createObjectURL(file),
            name: `slot${slotIndex}_${Math.floor(Math.random() * 0x10000).toString(16)}.jpg`,
            upload: "loading",
            slotIndex,
        };
        const other = currentDocs.find((d: any) => d.slotIndex === (slotIndex === 0 ? 1 : 0));
        const newDocs = slotIndex === 0
            ? [newDoc, ...(other ? [other] : [])]
            : [...(other ? [other] : []), newDoc];

        setDocuments("identity_card", newDocs);
        const formData = new FormData();
        formData.append("file", newDoc.file, newDoc.name);
        postDocumentImage(formData, newDoc.id, "identity_card");
    };

    const handleSlotFileSelect = (event: React.ChangeEvent<HTMLInputElement>, slotIndex: number) => {
        if (!event.target.files || event.target.files.length === 0) return;
        processAndUpload(event.target.files[0], slotIndex, activeDocs);
        event.target.value = "";
    };

    const handleDeleteSlot = (doc: any) => {
        if (doc?._id) {
            deleteDocument(doc._id, "identity_card");
        } else {
            deleteDocumentImage(doc.id, "identity_card");
        }
        setDocuments("identity_card", activeDocs.filter((d: any) => d !== doc));
    };

    useEffect(() => {
        if (fetchingDocuments === "failure") cleanGetDocuments();
        if (fetchingPostDocumentImage === "success") cleanGetDocuments();
    }, [fetchingDocuments]);

    useEffect(() => {
        if (fetchingPostDocumentImage === "failure") cleanPostDocumentImage();
        if (fetchingPostDocumentImage === "success") cleanPostDocumentImage();
    }, [fetchingPostDocumentImage]);

    useEffect(() => {
        const bothFilled = SLOTS.every((_, i) => activeDocs.find((d: any) => d.slotIndex === i));
        const hasNewDoc = activeDocs.some((d: any) => d?.id);
        const noneLoading = !activeDocs.some((d: any) => d?.upload === "loading");
        if (bothFilled && hasNewDoc && noneLoading) {
            const key = activeDocs.map((d: any) => d.id ?? d._id).sort().join(",");
            if (key !== savedKeyRef.current) {
                savedKeyRef.current = key;
                // Snapshot url → slot before the API call so that after the
                // reducer replaces local docs with the server response, useMemo
                // can recover the slot from the URL (which the API preserves).
                activeDocs.forEach((d: any) => {
                    if (d.url) urlSlotRef.current[d.url] = d.slotIndex;
                });
                postDocuments(activeDocs, "identity_card");
            }
        }
    }, [activeDocs]);

    useEffect(() => {
        if (fetchingPostDocuments === "failure") {
            toast("Ha ocurrido un error al guardar los cambios");
            cleanPostDocuments();
        }
        if (fetchingPostDocuments === "success") {
            getIsValidDocuments();
            toast("Se han actualizado los documentos correctamente");
            cleanPostDocuments();
        }
    }, [fetchingPostDocuments]);

    useEffect(() => {
        if (fetchingDeleteDocument === "failure") {
            toast("Error al eliminar el documento");
            cleanDeleteDocument();
        }
        if (fetchingDeleteDocument === "success") { getIsValidDocuments(); cleanDeleteDocument(); }
    }, [fetchingDeleteDocument]);

    useEffect(() => {
        if (fetchingDeleteDocumentImage === "failure") {
            toast("Error al eliminar el documento");
            cleanDeleteDocumentImage();
        }
        if (fetchingDeleteDocumentImage === "success") { getIsValidDocuments(); cleanDeleteDocumentImage(); }
    }, [fetchingDeleteDocumentImage]);

    useEffect(() => {
        if (types.data.length > 0 || types.fetching === "success") {
            getDocuments(documentType("identity_card"));
        }
    }, [types.fetching, location.pathname]);

    return (
        <div className={styles.slotsRow}>
            {SLOTS.map((slot, index) => {
                const doc = activeDocs.find((d: any) => d.slotIndex === index);
                const isBusy =
                    fetchingDeleteDocument === "loading" ||
                    fetchingDeleteDocumentImage === "loading" ||
                    fetchingPostDocuments === "loading";
                return (
                    <div key={slot.label} className={styles.slot}>
                        {doc ? (
                            <div className={styles.slotFilled}>
                                {doc.upload === "loading" && (
                                    <div className={styles.slotLoader}>
                                        <CircularProgress size={28} style={{ color: "#7100ff" }} />
                                    </div>
                                )}
                                <img
                                    src={doc.uri ?? doc.url ?? doc.previewURL ?? doc.path}
                                    className={styles.slotImage}
                                    alt={slot.label}
                                />
                                <button
                                    className={styles.deleteBtn}
                                    onClick={() => handleDeleteSlot(doc)}
                                    disabled={isBusy}
                                >
                                    <DeleteOutlineIcon className={styles.deleteBtnIcon} />
                                </button>
                            </div>
                        ) : (
                            <label className={styles.slotEmpty}>
                                <AddAPhotoIcon className={styles.slotEmptyIcon} />
                                <p className={styles.slotEmptyText}>
                                    Aún no cargaste el <strong>{slot.label}</strong> de tu cédula
                                </p>
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => handleSlotFileSelect(e, index)}
                                />
                            </label>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
export default Identity;
